import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { useParams, Link, useNavigate, useLocation as useReactLocation } from 'react-router-dom';

import { usePropertyStore } from '@/stores/property-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { useLogEntryStore } from '@/stores/log-entry-store';
import { useWeatherStore } from '@/stores/weather-store';
import { LogTimeline } from '@/components/scouting/LogTimeline';
import { SimilarDayInsight } from '@/components/patterns/SimilarDayInsight';
import { usePropertyInsights } from '@/stores/pattern-store';
import type { SimilarDayMatch } from '@/types/patterns';
import { HuntMap } from '@/components/map/HuntMap';
import { useMap } from '@/hooks/useMap';
import { CoordinateDisplay } from '@/components/map/CoordinateDisplay';
import { MapPickerModal } from '@/components/map/MapPickerModal';
import { BoundaryDrawingModal } from '@/components/map/BoundaryDrawingModal';
import { WaypointMetadataForm } from '@/components/waypoint/WaypointMetadataForm';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import { DataCockpit } from '@/components/common/DataCockpit';
import { Button } from '@/components/common/Button';
import { Textarea } from '@/components/common/Textarea';
import { WAYPOINT_TYPE_LABELS, WAYPOINT_TYPE_COLORS } from '@/components/map/map-utils';
import type { WaypointMetadata, CameraMetadata, BlindMetadata, FoodSourceMetadata, GeoJSONLineString, GeoJSONPolygon } from '@/types/waypoint';
import { WaypointType } from '@/types/waypoint';
import { CameraPhotoUpload } from '@/components/photos/CameraPhotoUpload';
import { PhotoGallery } from '@/components/photos/PhotoGallery';
import { getPhotosByWaypoint, deleteWaypointPhoto } from '@/services/photo-service';
import type { PhotoRecord } from '@/types/photo';
import { useAuthStore } from '@/stores/auth-store';
import { computeAcreage } from '@/utils/boundary-utils';
import { isWindFavorable } from '@/utils/wind-favorability';

// ---------------------------------------------------------------------------
// BoundaryPreviewLayer — adds a read-only polygon overlay to a HuntMap
// ---------------------------------------------------------------------------

interface BoundaryPreviewLayerProps {
  polygon: GeoJSONPolygon;
  color: string;
  /** Waypoint pin position [lat, lng] */
  center: [number, number];
}

function BoundaryPreviewLayer({ polygon, color, center }: BoundaryPreviewLayerProps) {
  const map = useMap();
  const markerRef = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    const SOURCE_ID = 'bdry-preview-src';
    const FILL_ID = 'bdry-preview-fill';
    const LINE_ID = 'bdry-preview-line';

    function setup() {
      try {
        if (!map.getSource(SOURCE_ID)) {
          map.addSource(SOURCE_ID, {
            type: 'geojson',
            data: { type: 'Feature', geometry: polygon, properties: {} },
          });
        }
        if (!map.getLayer(FILL_ID)) {
          map.addLayer({
            id: FILL_ID,
            type: 'fill',
            source: SOURCE_ID,
            paint: { 'fill-color': color, 'fill-opacity': 0.2 },
          });
        }
        if (!map.getLayer(LINE_ID)) {
          map.addLayer({
            id: LINE_ID,
            type: 'line',
            source: SOURCE_ID,
            paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.85 },
          });
        }

        // Fit to polygon bounds
        const coords = polygon.coordinates[0];
        const lngs = coords.map(([lng]) => lng);
        const lats = coords.map(([, lat]) => lat);
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 32, animate: false },
        );

        // Place waypoint pin
        if (!markerRef.current) {
          markerRef.current = new maplibregl.Marker({ color })
            .setLngLat([center[1], center[0]])
            .addTo(map);
        }
      } catch { /* map may not be ready or already destroyed */ }
    }

    map.on('style.load', setup);
    if (map.isStyleLoaded()) setup();

    return () => {
      map.off('style.load', setup);
      markerRef.current?.remove();
      markerRef.current = null;
      try {
        if (map.getLayer(LINE_ID)) map.removeLayer(LINE_ID);
        if (map.getLayer(FILL_ID)) map.removeLayer(FILL_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch { /* map destroyed */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polygon, color]);

  return null;
}

// ---------------------------------------------------------------------------
// Waypoint Pattern Insight — inner component so hook is always called
// ---------------------------------------------------------------------------

interface WaypointPatternInsightProps {
  propertyId: string;
  waypointId: string;
  propertyName?: string;
}

function WaypointPatternInsight({ propertyId, waypointId, propertyName }: WaypointPatternInsightProps) {
  const { insights, dataQuality } = usePropertyInsights(propertyId, propertyName);

  const match = insights?.insights.find(
    (i): i is SimilarDayMatch => i.type === 'similar_day' && i.waypointId === waypointId,
  );

  if (match) {
    return (
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4" data-testid="location-pattern-insight">
        <SimilarDayInsight match={match} />
      </section>
    );
  }

  if (dataQuality === 'insufficient' || dataQuality === null) {
    return (
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4" data-testid="location-pattern-insufficient">
        <p className="text-body-sm text-[var(--color-text-muted)]">
          Log more visits to unlock patterns for this location.
        </p>
      </section>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------

export default function WaypointDetailPage() {
  const { propertyId, waypointId } = useParams<{
    propertyId: string;
    waypointId: string;
  }>();
  const navigate = useNavigate();
  const reactLocation = useReactLocation();

  const userId = useAuthStore((s) => s.user?.id);
  const { properties, loadProperties } = usePropertyStore();
  const { waypoints, loadWaypoints, editWaypoint, removeWaypoint } = useWaypointStore();
  const { entries, loadEntriesByWaypoint } = useLogEntryStore();
  const { getConditions } = useWeatherStore();

  const hasMultiYearData = new Set(entries.map((e) => e.date.slice(0, 4))).size >= 2;
  // Note: multi-year scouting comparison is computed inline; comparison-utils has an equivalent helper if needed later.

  const [waypointName, setWaypointName] = useState('');
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [metadata, setMetadata] = useState<Partial<WaypointMetadata>>({});
  const [visibleOnMap, setVisibleOnMap] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [boundaryModalOpen, setBoundaryModalOpen] = useState(false);
  const [showClearBoundaryConfirm, setShowClearBoundaryConfirm] = useState(false);
  // Camera photos state (M42 — only used for camera-type waypoints)
  const [cameraPhotos, setCameraPhotos] = useState<PhotoRecord[]>([]);

  useEffect(() => {
    if (properties.length === 0) {
      void loadProperties();
    }
  }, [properties.length, loadProperties]);

  useEffect(() => {
    if (propertyId && waypoints.length === 0) {
      void loadWaypoints(propertyId);
    }
  }, [propertyId, waypoints.length, loadWaypoints]);

  useEffect(() => {
    if (waypointId) void loadEntriesByWaypoint(waypointId);
  }, [waypointId, loadEntriesByWaypoint]);

  const property = properties.find((p) => p.id === propertyId);
  const waypoint = waypoints.find((w) => w.id === waypointId);

  // Wind favorability — read current wind from weather-store for this property
  // (weather-store.getConditions returns null when weather data is not yet loaded/unavailable)
  const currentWindDirection = useMemo(
    () => (propertyId ? (getConditions(propertyId)?.windDirection ?? undefined) : undefined),
    [propertyId, getConditions],
  );

  // Load camera photos when viewing a camera-type waypoint
  useEffect(() => {
    if (waypointId && waypoint?.type === WaypointType.Camera) {
      getPhotosByWaypoint(waypointId)
        .then(setCameraPhotos)
        .catch((err) => console.error('WaypointDetailPage: failed to load camera photos', err));
    }
  }, [waypointId, waypoint?.type]);

  // Sync local state when waypoint loads
  useEffect(() => {
    if (waypoint) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
      setWaypointName(waypoint.name);
      setCoordinates({ lat: waypoint.latitude, lng: waypoint.longitude });
      setNotes(waypoint.notes ?? '');
      setMetadata(waypoint.metadata ?? {});
      setVisibleOnMap(waypoint.visibleOnMap !== false);
    }
  }, [waypoint?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle trail result returned from TrailDrawPage
  useEffect(() => {
    const state = reactLocation.state as { trailResult?: GeoJSONLineString } | null;
    if (!state?.trailResult) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
    setMetadata((prev) => ({ ...prev, pathGeoJSON: state.trailResult }));
    navigate(reactLocation.pathname, { replace: true, state: {} });
  }, [reactLocation.state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!propertyId || !waypointId) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-bark-400 text-body-sm">Invalid waypoint URL.</p>
        <Link
          to="/properties"
          className="text-ember-400 text-body-sm hover:text-ember-300 transition-colors"
        >
          ← Back to Properties
        </Link>
      </div>
    );
  }

  if (!waypoint) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-bark-400 text-body-sm">Waypoint not found.</p>
        <Link
          to={`/properties/${propertyId}`}
          className="text-ember-400 text-body-sm hover:text-ember-300 transition-colors"
        >
          ← Back to Property
        </Link>
      </div>
    );
  }

  const typeLabel = WAYPOINT_TYPE_LABELS[waypoint.type];
  const typeColor = WAYPOINT_TYPE_COLORS[waypoint.type];

  async function handleSave() {
    if (!waypoint) return;
    setIsSaving(true);
    try {
      await editWaypoint(waypoint.id, {
        name: waypointName.trim() || waypoint.name,
        notes,
        metadata: metadata as WaypointMetadata,
        visibleOnMap,
        ...(coordinates ? { latitude: coordinates.lat, longitude: coordinates.lng } : {}),
      });
      void navigate(`/properties/${propertyId}`, { replace: true });
    } catch (err) {
      console.error('WaypointDetailPage.handleSave:', err);
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!waypoint) return;
    setIsDeleting(true);
    try {
      await removeWaypoint(waypoint.id);
      void navigate(`/properties/${propertyId}`, { replace: true });
    } finally {
      setIsDeleting(false);
    }
  }

  // Boundary helpers — only relevant for FoodSource waypoints
  const meta = metadata as Record<string, unknown>;
  const boundaryGeoJSON = meta.boundaryGeoJSON as GeoJSONPolygon | undefined;
  const hasBoundary = !!boundaryGeoJSON;
  const boundaryAcres = hasBoundary
    ? computeAcreage(
        boundaryGeoJSON.coordinates[0].map(([lng, lat]) => [lat, lng] as [number, number]),
      )
    : null;

  // Convert GeoJSON polygon to vertex array for BoundaryDrawingModal ([lat, lng] pairs)
  const boundaryVertices: [number, number][] | undefined = hasBoundary
    ? boundaryGeoJSON.coordinates[0]
        .slice(0, -1) // drop closing duplicate vertex
        .map(([lng, lat]) => [lat, lng] as [number, number])
    : undefined;

  function handleBoundarySave(vertices: [number, number][]) {
    // Convert [lat, lng] vertex array → GeoJSONPolygon (closes the ring)
    const coords: [number, number][] = vertices.map(([lat, lng]) => [lng, lat]);
    const closed: [number, number][] = [...coords, coords[0]];
    const polygon: GeoJSONPolygon = { type: 'Polygon', coordinates: [closed] };
    const acresCalc = computeAcreage(vertices);
    setMetadata((prev) => ({
      ...prev,
      boundaryGeoJSON: polygon,
      plotSizeAcresCalculated: acresCalc,
    }));
    setBoundaryModalOpen(false);
  }

  function handleClearBoundary() {
    setMetadata((prev) => {
      const next = { ...(prev as Record<string, unknown>) };
      delete next.boundaryGeoJSON;
      delete next.plotSizeAcresCalculated;
      return next as Partial<WaypointMetadata>;
    });
    setShowClearBoundaryConfirm(false);
  }

  const isFoodSource = waypoint.type === WaypointType.FoodSource;
  const showBoundarySection = isFoodSource || hasBoundary;

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      {/* Back + header */}
      <div className="flex flex-col gap-1">
        <Breadcrumb
          label={property?.name ?? 'Property'}
          to={`/properties/${propertyId}`}
        />
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {/* Waypoint name — editable inline */}
          <input
            type="text"
            value={waypointName}
            onChange={(e) => setWaypointName(e.target.value)}
            aria-label="Waypoint name"
            className="font-display text-display-lg text-[var(--color-text-display)] bg-transparent border-b border-transparent hover:border-[var(--color-border-subtle)] focus:border-[var(--color-accent)] focus:outline-none transition-colors min-w-0 flex-1"
          />
          {/* Type badge — read-only, type cannot be changed after creation */}
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-body-xs font-medium border border-[var(--color-border-subtle)] text-[var(--color-text-muted)] shrink-0">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: typeColor }}
              aria-hidden="true"
            />
            {typeLabel}
          </span>
        </div>
      </div>

      {/* Coordinate display + map picker */}
      <CoordinateDisplay
        lat={coordinates?.lat ?? null}
        lng={coordinates?.lng ?? null}
        onPickLocation={() => setMapPickerOpen(true)}
        label="Location"
      />

      <MapPickerModal
        isOpen={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        onConfirm={(coords) => {
          setCoordinates(coords);
          setMapPickerOpen(false);
        }}
        initialCoordinates={coordinates ?? undefined}
        title="Pick Location"
      />

      {/* Boundary section — only for FoodSource waypoints or when a boundary already exists */}
      {showBoundarySection && (
        <>
          <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card overflow-hidden flex flex-col gap-0" data-testid="boundary-section">
            {/* Boundary mini-map — only when a polygon exists */}
            {hasBoundary && coordinates && (
              <div
                className="border-[0.5px] border-[#2d2a26] shadow-card-md rounded-t-lg overflow-hidden h-48"
                style={{ height: '192px' }}
                data-testid="boundary-mini-map"
              >
                <HuntMap
                  center={[coordinates.lat, coordinates.lng]}
                  zoom={15}
                  className="h-full w-full"
                  initialStyle="satellite"
                  requireTapToInteract
                >
                  <BoundaryPreviewLayer
                    polygon={boundaryGeoJSON}
                    color={typeColor}
                    center={[coordinates.lat, coordinates.lng]}
                  />
                </HuntMap>
              </div>
            )}

            <div className="p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h2 className="field-label">
                  Boundary
                </h2>
                {hasBoundary && boundaryAcres !== null && (
                  <span className="text-body-xs font-medium bg-[var(--color-elevated)] border border-[var(--color-border)] px-2 py-0.5 rounded text-[var(--color-text-secondary)]" data-testid="acreage-badge">
                    ~{boundaryAcres.toFixed(1)} acres
                  </span>
                )}
              </div>

              {hasBoundary ? (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setBoundaryModalOpen(true)}
                    data-testid="edit-boundary-button"
                  >
                    Edit Boundary
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowClearBoundaryConfirm(true)}
                    className="text-body-sm text-[var(--color-text-placeholder)] hover:text-[var(--color-accent)] transition-colors"
                    data-testid="clear-boundary-button"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-body-xs text-[var(--color-text-placeholder)]">Draw a boundary to calculate area</p>
                  <Button
                    variant="ghost"
                    onClick={() => setBoundaryModalOpen(true)}
                    data-testid="draw-boundary-button"
                  >
                    Draw Boundary
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* Boundary drawing modal */}
          {boundaryModalOpen && coordinates && (
            <BoundaryDrawingModal
              isOpen={boundaryModalOpen}
              onClose={() => setBoundaryModalOpen(false)}
              onSave={handleBoundarySave}
              center={[coordinates.lat, coordinates.lng]}
              initialVertices={boundaryVertices}
            />
          )}

          {/* Clear boundary confirm */}
          {showClearBoundaryConfirm && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
              role="dialog"
              aria-modal="true"
            >
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 max-w-sm w-full shadow-lg">
                <h2 className="font-display text-display-sm text-[var(--color-text-display)] mb-2">Clear boundary?</h2>
                <p className="text-body-sm text-[var(--color-text-muted)] mb-5">
                  This will remove the drawn boundary and calculated acreage. You can redraw it later.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setShowClearBoundaryConfirm(false)}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={handleClearBoundary} data-testid="confirm-clear-boundary">
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Wind favorability — Blind/Stand waypoints only.
          I-9: WindCompassRose SVG gauge removed — duplicate of the editable compass inside
          BlindForm's Details section. The favorability label (FAVORABLE/UNFAVORABLE) is kept
          because it derives from live weather data and is the unique value-add of this section. */}
      {waypoint.type === WaypointType.Blind && (
        <section
          className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-2"
          data-testid="wind-favorability-section"
        >
          <h2 className="field-label">
            WIND
          </h2>
          {/* Favorability label */}
          {((): React.ReactNode => {
            const idealDirs = (metadata as Partial<BlindMetadata>).idealWindDirections ?? [];
            const favorability = isWindFavorable(currentWindDirection, idealDirs);
            if (favorability === 'favorable') {
              return (
                <p
                  className="text-body-xs font-medium text-green-400"
                  data-testid="wind-favorability-label"
                  data-favorability="favorable"
                >
                  ✓ FAVORABLE
                </p>
              );
            }
            if (favorability === 'unfavorable') {
              return (
                <p
                  className="text-body-xs font-medium text-[var(--color-accent)]"
                  data-testid="wind-favorability-label"
                  data-favorability="unfavorable"
                >
                  ✗ UNFAVORABLE
                </p>
              );
            }
            return (
              <p
                className="text-body-xs text-[var(--color-text-placeholder)]"
                data-testid="wind-favorability-label"
                data-favorability="unknown"
              >
                Current wind unavailable
              </p>
            );
          })()}
        </section>
      )}

      {/* Details / metadata form */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4" data-testid="details-section">
        <h2 className="field-label mb-4">
          DETAILS
        </h2>
        {/* Type-specific metadata DataCockpit */}
        {waypoint.type === WaypointType.Blind && (() => {
          const m = metadata as Partial<BlindMetadata>;
          const cells = [
            { primary: m.blindType ? m.blindType.replace(/_/g, ' ') : '—', label: 'TYPE', testId: 'cockpit-blind-type' },
            {
              primary: m.capacity ? String(m.capacity) : '—',
              label: 'CAPACITY',
              testId: 'cockpit-blind-capacity',
            },
            {
              primary: m.bestTimeOfDay
                ? (m.bestTimeOfDay === 'all_day' ? 'All Day' : m.bestTimeOfDay === 'morning' ? 'Morning' : 'Evening')
                : '—',
              label: 'BEST TIME',
              testId: 'cockpit-blind-best-time',
            },
            {
              primary: m.idealWindDirections?.length ? m.idealWindDirections.join(', ') : '—',
              label: 'IDEAL WIND',
              testId: 'cockpit-blind-ideal-wind',
            },
          ];
          return (
            <div className="border-[0.5px] border-[var(--color-border)] rounded-lg overflow-hidden mb-4" data-testid="waypoint-metadata-cockpit">
              <DataCockpit cells={cells} />
            </div>
          );
        })()}
        {waypoint.type === WaypointType.FoodSource && (() => {
          const m = metadata as Partial<FoodSourceMetadata>;
          const plantedSpecies = ('plantedSpecies' in (m ?? {}) ? (m as { plantedSpecies?: string[] }).plantedSpecies : undefined);
          const soilPH = ('soilPH' in (m ?? {}) ? (m as { soilPH?: number }).soilPH : undefined);
          const soilTestDate = ('soilTestDate' in (m ?? {}) ? (m as { soilTestDate?: string }).soilTestDate : undefined);
          const plotSizeAcres =
            ('plotSizeAcresCalculated' in (m ?? {}) ? (m as { plotSizeAcresCalculated?: number }).plotSizeAcresCalculated : undefined) ??
            ('plotSizeAcres' in (m ?? {}) ? (m as { plotSizeAcres?: number }).plotSizeAcres : undefined);
          const cells = [
            {
              primary: plantedSpecies?.length ? plantedSpecies.slice(0, 2).join(', ').replace(/_/g, ' ') : '—',
              label: 'PLANTED',
              testId: 'cockpit-plot-planted',
            },
            {
              primary: plotSizeAcres ? `${Number(plotSizeAcres).toFixed(1)} ac` : '—',
              label: 'PLOT SIZE',
              testId: 'cockpit-plot-size',
            },
            {
              primary: soilPH !== undefined && soilPH !== null ? String(soilPH) : '—',
              label: 'SOIL PH',
              testId: 'cockpit-plot-soil-ph',
            },
            {
              primary: soilTestDate ? soilTestDate.slice(0, 7) : '—',
              label: 'LAST SOIL TEST',
              testId: 'cockpit-plot-soil-test',
            },
          ];
          return (
            <div className="border-[0.5px] border-[var(--color-border)] rounded-lg overflow-hidden mb-4" data-testid="waypoint-metadata-cockpit">
              <DataCockpit cells={cells} />
            </div>
          );
        })()}
        {waypoint.type === WaypointType.Camera && (() => {
          const m = metadata as Partial<CameraMetadata>;
          const cells = [
            {
              primary: m.cloudPlatform && m.cloudPlatform !== 'none'
                ? m.cloudPlatform.charAt(0).toUpperCase() + m.cloudPlatform.slice(1)
                : '—',
              label: 'PLATFORM',
              testId: 'cockpit-camera-platform',
            },
            {
              primary: m.cloudCameraId ?? '—',
              label: 'CAMERA ID',
              testId: 'cockpit-camera-id',
            },
            {
              primary: m.cameraType === 'cellular' ? 'Cellular' : m.cameraType === 'sd_card' ? 'SD Card' : '—',
              label: 'TYPE',
              testId: 'cockpit-camera-type',
            },
            {
              primary: m.facing ?? '—',
              label: 'FACING',
              testId: 'cockpit-camera-facing',
            },
          ];
          return (
            <div className="border-[0.5px] border-[var(--color-border)] rounded-lg overflow-hidden mb-4" data-testid="waypoint-metadata-cockpit">
              <DataCockpit cells={cells} />
            </div>
          );
        })()}
        <WaypointMetadataForm
          type={waypoint.type}
          metadata={metadata}
          onChange={setMetadata}
          propertyId={propertyId ?? ''}
          centerCoords={
            coordinates && waypoint.type !== WaypointType.FoodSource
              ? [coordinates.lat, coordinates.lng]
              : undefined
          }
          hideWindCompass={false}
        />
      </section>

      {/* WaypointMetadataForm renders the editable wind compass for blind types via BlindForm.
          The read-only WindCompassRose gauge that previously appeared in `wind-favorability-section`
          (M70) was removed in Defect Batch I-9 to eliminate duplicate compasses on the page.
          Only the favorability label remains in that section. */}

      {/* Map visibility toggle */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="field-label">
              Show on Map
            </p>
            <p className="text-body-xs text-[var(--color-text-placeholder)] mt-0.5">
              Toggle off to hide this waypoint on the map
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={visibleOnMap}
            onClick={() => setVisibleOnMap((v) => !v)}
            data-testid="visibility-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              visibleOnMap ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border)]'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                visibleOnMap ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </section>

      {/* Notes */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4">
        <Textarea
          id="waypoint-notes"
          label="Notes"
          value={notes}
          onChange={setNotes}
          placeholder="General notes about this waypoint..."
          rows={4}
        />
      </section>

      {/* Coordinates — system data, rendered last (muted, small) */}
      {coordinates && (
        <p
          className="text-body-xs text-[var(--color-text-placeholder)] text-center"
          data-testid="waypoint-coordinates"
        >
          {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
        </p>
      )}

      {/* Save / Delete actions */}
      <div className="flex gap-3">
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="flex-1 justify-center"
        >
          {isSaving ? 'Saving…' : 'Save Changes'}
        </Button>
        <Button
          variant="danger"
          onClick={() => setShowConfirmDelete(true)}
          disabled={isDeleting}
        >
          Delete
        </Button>
      </div>

      {/* Compare button — only shown when entries span 2+ calendar years */}
      {hasMultiYearData && (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() =>
              navigate(`/properties/${propertyId}/waypoints/${waypointId}/compare`)
            }
            data-testid="compare-button"
          >
            Compare Years
          </Button>
          <span className="text-body-xs text-[var(--color-text-muted)]">
            View year-over-year scouting data
          </span>
        </div>
      )}

      {/* Waypoint pattern insight */}
      {propertyId && waypointId && (
        <WaypointPatternInsight
          propertyId={propertyId}
          waypointId={waypointId}
          propertyName={property?.name}
        />
      )}

      {/* Trail cam photos — camera-type waypoints only (M42) */}
      {waypoint.type === WaypointType.Camera && (
        <section
          className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-3"
          data-testid="camera-photos-section"
        >
          <h2 className="field-label">
            Trail Cam Photos {cameraPhotos.length > 0 && `(${cameraPhotos.length})`}
          </h2>
          {userId && (
            <CameraPhotoUpload
              waypointId={waypointId}
              userId={userId}
              cameraMetadata={metadata.type === 'camera' ? (metadata as CameraMetadata) : { type: 'camera', cameraType: 'sd_card' }}
              onPhotosAdded={(newPhotos) => setCameraPhotos((prev) => [...prev, ...newPhotos])}
              onMetadataUpdate={(updates) => setMetadata((prev) => ({ ...prev, ...updates }))}
            />
          )}
          {cameraPhotos.length > 0 && (
            <PhotoGallery
              photos={cameraPhotos}
              editable
              onDelete={async (photoId) => {
                await deleteWaypointPhoto(photoId);
                setCameraPhotos((prev) => prev.filter((p) => p.id !== photoId));
              }}
            />
          )}
        </section>
      )}

      {/* Scouting log */}
      <LogTimeline
        entries={entries}
        onEntryClick={(id) => navigate(`/waypoints/${waypointId}/log/${id}`)}
        onAddClick={() => navigate(`/waypoints/${waypointId}/log/new`)}
        emptyVariant="location"
      />

      {/* Delete confirmation dialog */}
      {showConfirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-waypoint-title"
        >
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-6 max-w-sm w-full shadow-lg">
            <h2
              id="confirm-delete-waypoint-title"
              className="font-display text-display-sm text-[var(--color-text-display)] mb-2"
            >
              Delete &ldquo;{waypoint.name}&rdquo;?
            </h2>
            <p className="text-body-sm text-[var(--color-text-muted)] mb-5">
              This will permanently delete this waypoint and all its scouting log entries. This
              cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => setShowConfirmDelete(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => void handleDelete()}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete waypoint'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
